import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axios";
import video from "../../Assets/videoes/video4.mp4";
import { Volume2, VolumeX } from "lucide-react"; // icons for sound button

const BlogHeroSection = () => {
  const [loading, setLoading] = useState(true);
  const [blogPosts, setBlogPosts] = useState([]);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = useRef(null);
  const navigate = useNavigate();

  // Fetch blogs from backend
  const fetchBlogs = async () => {
    try {
      const res = await axiosInstance.get("/blogs/get-all");
      setBlogPosts(res.data || []);
    } catch (error) {
      console.error("Failed to fetch blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();

    // Try autoplay
    const playVideo = async () => {
      try {
        if (videoRef.current) {
          videoRef.current.muted = isMuted;
          await videoRef.current.play();
        }
      } catch (err) {
        console.warn("Autoplay blocked:", err);
        setIsPlaying(false);
      }
    };

    playVideo();
  }, [isMuted]);

  // Toggle Play/Pause on video click
  const handleVideoClick = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Toggle Mute/Unmute
  const handleMuteToggle = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  return (
    <div className="flex flex-col lg:flex-row w-full p-4 md:p-8 lg:p-10 gap-6">
      {/* Left Side: Video */}
      <div className="lg:w-1/3 w-full relative">
        <video
          ref={videoRef}
          src={video}
          autoPlay
          loop
          muted={isMuted}
          playsInline
          onClick={handleVideoClick} // click to toggle play/pause
          className="w-full object-cover rounded-lg shadow-md cursor-pointer"
          controls={false}
          controlsList="nodownload nofullscreen noremoteplayback"
          disablePictureInPicture
        />

        {/* Sound Toggle Button */}
        <button
          onClick={handleMuteToggle}
          className="absolute bottom-3 right-3 bg-black/60 p-2 rounded-full text-white hover:bg-black/80"
        >
          {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
      </div>

      {/* Right Side: Blogs */}
      <div className="lg:w-2/3 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {loading
            ? Array(6)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse overflow-hidden rounded-lg shadow-md bg-gray-200 flex flex-col h-80"
                  >
                    <div className="h-40 bg-gray-300"></div>
                    <div className="p-4 flex flex-col gap-3">
                      <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                      <div className="h-4 bg-gray-300 rounded w-1/3"></div>
                    </div>
                  </div>
                ))
            : blogPosts.map((post, index) => (
                <div
                  key={index}
                  className="group overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all duration-300 bg-white flex flex-col relative"
                >
                  {/* Blog Image */}
                  <div className="overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-40 object-cover"
                    />
                  </div>

                  {/* Date */}
                  <div className="absolute w-12 h-12 rounded-md top-28 left-2 text-center bg-orange-600 text-white flex justify-center items-center flex-col font-bold">
                    <p className="text-xs">
                      {new Date(post.createdAt).getDate()}
                    </p>
                    <p className="text-[10px]">
                      {new Date(post.createdAt).toLocaleString("default", {
                        month: "short",
                      })}
                    </p>
                  </div>

                  {/* Blog Content */}
                  <div className="px-4 py-2">
                    <h3 className="font-semibold text-base text-[#1A1A1A] line-clamp-2">
                      {post.title}
                    </h3>

                    {/* Author Name */}
                    <p className="text-xs text-gray-500 mb-2">
                      By{" "}
                      <span className="font-medium">
                        {post.author || "Admin"}
                      </span>
                    </p>

                    <button
                      onClick={() => {
                        navigate(`/blog-Details/${post._id}`);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className="text-[#E8471B] font-medium text-sm transition-colors cursor-pointer"
                    >
                      Read More...
                    </button>
                  </div>
                </div>
              ))}
        </div>
      </div>
    </div>
  );
};

export default BlogHeroSection;
